import type {
  Device,
  DeviceCommand,
  DeviceCommandKind,
  DeviceCounts,
  DeviceIdentityPatch,
  DeviceListQuery,
  ScriptInfo,
} from '../models/device';
import type { Paged } from '../models/common';
import { Store } from '../core/store';
import { api, PANEL_API } from './api-client';

/** Kayıtlı toplam cihaz — sidebar rozeti; her listelemede tazelenir. */
export const fleetTotal = new Store<number | null>(null);

/** Liste yanıtı: sayfa + durum çipi sayaçları (q+marka filtreli, durum hariç). */
export interface DeviceListResponse extends Paged<Device> {
  counts: DeviceCounts;
  /** Filtresiz kayıtlı toplam cihaz (sidebar rozeti) */
  fleetTotal: number;
}

/** Filtre + sıralama parametreleri (liste ve dışa aktarma aynı sözleşmeyi kullanır). */
function filterParams(query: DeviceListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.status && query.status !== 'all') params.set('status', query.status);
  if (query.brand && query.brand !== 'all') params.set('brand', query.brand);
  if (query.sort) {
    params.set('sort', query.sort);
    params.set('dir', query.dir ?? 'asc');
  }
  return params;
}

class DeviceService {
  async list(query: DeviceListQuery): Promise<DeviceListResponse> {
    const params = filterParams(query);
    params.set('page', String(query.page ?? 1));
    params.set('page_size', String(query.pageSize ?? 25));
    const res = await api.get<DeviceListResponse>(`${PANEL_API}/devices?${params}`);
    fleetTotal.set(res.fleetTotal);
    return res;
  }

  /**
   * Gridin o anki halini (arama + durum + marka + sıralama) CSV olarak indirir —
   * SAYFASIZ: filtreye uyan tüm cihazlar. Sunucu dosya adını ve UTF-8 BOM'u verir;
   * oturum çerezi aynı köken olduğundan düz bir link indirmesi yeterlidir.
   */
  exportCsvUrl(query: DeviceListQuery): string {
    return `${PANEL_API}/devices/export.csv?${filterParams(query)}`;
  }

  downloadCsv(query: DeviceListQuery): void {
    const a = document.createElement('a');
    a.href = this.exportCsvUrl(query);
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 1000);
  }

  async get(serial: string): Promise<Device> {
    return api.get<Device>(`${PANEL_API}/devices/${encodeURIComponent(serial)}`);
  }

  async updateIdentity(serial: string, patch: DeviceIdentityPatch): Promise<Device> {
    return api.put<Device>(`${PANEL_API}/devices/${encodeURIComponent(serial)}`, patch);
  }

  /** Tek cihaza komut: restart / rescue / force_install / run_script / set_agc. */
  async command(
    serial: string,
    kind: DeviceCommandKind,
    payload?: Record<string, unknown>,
  ): Promise<void> {
    await api.post(`${PANEL_API}/devices/${encodeURIComponent(serial)}/commands`, {
      kind,
      payload,
    });
  }

  /** Toplu komut. */
  async bulkCommand(
    serials: string[],
    kind: DeviceCommandKind,
    payload?: Record<string, unknown>,
  ): Promise<void> {
    await api.post(`${PANEL_API}/devices/commands`, { serials, kind, payload });
  }

  /** Cihazın son komutları + sonuçları. */
  async commands(serial: string): Promise<DeviceCommand[]> {
    const res = await api.get<{ commands: DeviceCommand[] }>(
      `${PANEL_API}/devices/${encodeURIComponent(serial)}/commands`,
    );
    return res.commands;
  }

  /** Script kütüphanesi. */
  async scripts(): Promise<ScriptInfo[]> {
    const res = await api.get<{ scripts: ScriptInfo[] }>(`${PANEL_API}/scripts`);
    return res.scripts;
  }

  async scriptBody(name: string): Promise<string> {
    const res = await api.get<{ content: string }>(`${PANEL_API}/scripts/${encodeURIComponent(name)}`);
    return res.content;
  }

  async saveScript(name: string, content: string): Promise<void> {
    await api.put(`${PANEL_API}/scripts/${encodeURIComponent(name)}`, { content });
  }

  async deleteScript(name: string): Promise<void> {
    await api.delete(`${PANEL_API}/scripts/${encodeURIComponent(name)}`);
  }

  /** Cihazı filodan sil (DB + WG peer). Cihaz çalışıyorsa yeniden kaydolur. */
  async remove(serial: string): Promise<void> {
    await api.delete(`${PANEL_API}/devices/${encodeURIComponent(serial)}`);
  }

  /** Seçili cihazlara imaj profili ata (tek cihaz = tek elemanlı liste). */
  async assign(serials: string[], brand: string): Promise<void> {
    await api.post(`${PANEL_API}/devices/assign`, { serials, brand });
  }

  /** Filtre kapsamındaki TÜM serileri döner ("Filtredeki tüm cihazlar" seçimi). */
  async listSerials(query: DeviceListQuery): Promise<string[]> {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.status && query.status !== 'all') params.set('status', query.status);
    if (query.brand && query.brand !== 'all') params.set('brand', query.brand);
    const res = await api.get<{ serials: string[] }>(`${PANEL_API}/devices/serials?${params}`);
    return res.serials;
  }
}

export const devices = new DeviceService();
